/**
 * Reader conveniences (bundled into theme.js):
 *
 *  - Saved recipes ♡ — no account: IDs in localStorage, count in the header,
 *    cards on the "My recipes" page fetched from winonweb/v1/cards.
 *  - Recently viewed — recorded on recipe pages; shown on "My recipes" and
 *    on the home page.
 *  - Share — the system share sheet on phones, "copy link" elsewhere.
 *  - Instant search — suggestions while typing (ARIA 1.2 combobox).
 *
 * One ID per recipe for all languages (the translation group), so saving in
 * Ukrainian also shows the recipe as saved in English. Nothing leaves the
 * browser except the IDs sent to render cards.
 */

import { MENU, MAX_MENU, readMenu, writeMenu } from './menu-store';
import initPantry from './pantry';
import initMenu from './menu';
import initPwa, { keepOffline } from './pwa';
import track from './track';

const SAVED = 'smakota-saved';
const RECENT = 'smakota-recent';
const MAX_SAVED = 100;
const MAX_RECENT = 12;

const cfg = window.winonweb || {};
const t = cfg.i18n || {};

/* -------------------------------------------------------------------- */
/* Storage                                                               */
/* -------------------------------------------------------------------- */

function readList( key ) {
	try {
		const v = JSON.parse( window.localStorage.getItem( key ) || '[]' );
		return Array.isArray( v ) ? v.map( Number ).filter( Boolean ) : [];
	} catch ( e ) {
		return [];
	}
}

function writeList( key, list ) {
	try {
		window.localStorage.setItem( key, JSON.stringify( list ) );
	} catch ( e ) {
		// Storage blocked: the feature simply does not persist.
	}
}

/* -------------------------------------------------------------------- */
/* Helpers                                                               */
/* -------------------------------------------------------------------- */

function announce( el, text ) {
	if ( ! el ) {
		return;
	}
	el.textContent = '';
	window.setTimeout( () => {
		el.textContent = text;
	}, 50 );
}

async function copyText( text ) {
	try {
		await navigator.clipboard.writeText( text );
		return true;
	} catch ( e ) {
		const area = document.createElement( 'textarea' );
		area.value = text;
		area.setAttribute( 'readonly', '' );
		area.style.position = 'fixed';
		area.style.opacity = '0';
		document.body.append( area );
		area.select();
		let ok = false;
		try {
			ok = document.execCommand( 'copy' );
		} catch ( err ) {
			ok = false;
		}
		area.remove();
		return ok;
	}
}

const LINK_ICON =
	'<svg class="icon" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1"/><path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1"/></svg>';

// Phones and tablets only: touch laptops (Windows) have a mouse too and get "copy link".
const isTouch = () =>
	window.matchMedia( '(hover: none) and (pointer: coarse)' ).matches;

/**
 * Card markup for recipe IDs, in the page language, in the given order.
 *
 * @param {number[]} ids Recipe (translation group) IDs.
 */
async function fetchCards( ids ) {
	if ( ! ids.length || ! cfg.rest ) {
		return [];
	}
	const url = `${ cfg.rest }cards?ids=${ ids.join(
		','
	) }&lang=${ encodeURIComponent( cfg.lang || '' ) }`;
	const res = await fetch( url, { headers: { Accept: 'application/json' } } );
	if ( ! res.ok ) {
		throw new Error( String( res.status ) );
	}
	const data = await res.json();
	return Array.isArray( data.items ) ? data.items : [];
}

/* -------------------------------------------------------------------- */
/* Header count                                                          */
/* -------------------------------------------------------------------- */

function updateSavedCount() {
	const n = readList( SAVED ).length;
	document.querySelectorAll( '[data-saved-count]' ).forEach( ( el ) => {
		el.textContent = n > 99 ? '99+' : String( n );
		el.hidden = n === 0;
	} );
	document.querySelectorAll( '[data-saved-label]' ).forEach( ( el ) => {
		el.textContent =
			n && t.myRecipesN
				? t.myRecipesN.replace( '{n}', String( n ) )
				: t.myRecipes || el.textContent;
	} );
}

/* -------------------------------------------------------------------- */
/* Recipe page: record visit, save, share                                */
/* -------------------------------------------------------------------- */

function initRecipePage() {
	const root = document.querySelector( '[data-recipe][data-group]' );
	if ( ! root ) {
		return;
	}
	const group = Number( root.dataset.group );
	const status = root.querySelector( '[data-cook-status]' );

	// Recently viewed: newest first, unique, capped.
	if ( group ) {
		writeList(
			RECENT,
			[
				group,
				...readList( RECENT ).filter( ( id ) => id !== group ),
			].slice( 0, MAX_RECENT )
		);
	}

	const save = root.querySelector( '[data-save]' );
	if ( save && group ) {
		const paint = () =>
			save.setAttribute(
				'aria-pressed',
				String( readList( SAVED ).includes( group ) )
			);
		save.hidden = false;
		paint();
		save.addEventListener( 'click', () => {
			const list = readList( SAVED );
			const on = ! list.includes( group );
			writeList(
				SAVED,
				on
					? [ group, ...list ].slice( 0, MAX_SAVED )
					: list.filter( ( id ) => id !== group )
			);
			paint();
			if ( on ) {
				save.classList.remove( 'is-pop' ); // Restart the heart "pop" (recipe.scss).
				void save.offsetWidth; // eslint-disable-line no-void
				save.classList.add( 'is-pop' );
			}
			updateSavedCount();
			keepOffline( window.location.href.split( '#' )[ 0 ], on ); // Saved = readable offline.
			announce( status, on ? t.savedMsg : t.removedMsg );
			track( on ? 'recipe_save' : 'recipe_unsave', { recipe_id: group } );
		} );
		window.addEventListener(
			'storage',
			( e ) => e.key === SAVED && paint()
		);
	}

	// Weekly menu: remembers the recipe with the servings chosen on the page.
	const menu = root.querySelector( '[data-menu]' );
	if ( menu && group ) {
		const inMenu = () => readMenu().some( ( m ) => m.g === group );
		const paint = () =>
			menu.setAttribute( 'aria-pressed', String( inMenu() ) );
		menu.hidden = false;
		paint();
		menu.addEventListener( 'click', () => {
			const list = readMenu();
			const on = ! inMenu();
			writeMenu(
				on
					? [
							...list,
							{
								g: group,
								f: Number( root.dataset.scale ) || 1,
								d: -1,
							},
					  ].slice( -MAX_MENU )
					: list.filter( ( m ) => m.g !== group )
			);
			paint();
			track( on ? 'menu_add' : 'menu_remove', { recipe_id: group } );
			if ( status ) {
				status.textContent = '';
				window.setTimeout( () => {
					status.replaceChildren(
						document.createTextNode(
							on ? t.menuAdded : t.menuRemoved
						)
					);
					if ( on && cfg.menuUrl ) {
						const a = document.createElement( 'a' );
						a.href = cfg.menuUrl;
						a.textContent = ` ${ t.openMenu } →`;
						status.append( a );
					}
				}, 50 );
			}
		} );
		window.addEventListener(
			'storage',
			( e ) => e.key === MENU && paint()
		);
	}

	const pin = root.querySelector( '[data-pin]' );
	if ( pin ) {
		pin.addEventListener( 'click', () =>
			track( 'recipe_share', { method: 'pinterest', recipe_id: group } )
		);
	}

	const share = root.querySelector( '[data-share]' );
	if ( share ) {
		// Say what the button will do: share sheet on phones, "copy link" elsewhere.
		if ( ! ( navigator.share && isTouch() ) && t.copyLink ) {
			[ ...share.childNodes ]
				.filter( ( n ) => n.nodeType === 3 )
				.forEach( ( n ) => n.remove() );
			share.append( document.createTextNode( t.copyLink ) );
			const icon = share.querySelector( 'svg' );
			if ( icon ) {
				icon.outerHTML = LINK_ICON; // Static markup.
			}
		}
		share.hidden = false;
		share.addEventListener( 'click', async () => {
			const url = window.location.href.split( '#' )[ 0 ];
			const title =
				( root.querySelector( 'h1' ) || {} ).textContent ||
				document.title;
			if ( navigator.share && isTouch() ) {
				try {
					await navigator.share( { title: title.trim(), url } );
					track( 'recipe_share', {
						method: 'share_sheet',
						recipe_id: group,
					} );
					return;
				} catch ( e ) {
					if ( e && e.name === 'AbortError' ) {
						return;
					}
				}
			}
			const copied = await copyText( url );
			announce( status, copied ? t.linkCopied : t.copyFailed );
			if ( copied ) {
				track( 'recipe_share', {
					method: 'copy_link',
					recipe_id: group,
				} );
			}
		} );
	}
}

/* -------------------------------------------------------------------- */
/* "My recipes" page + home "recently viewed"                            */
/* -------------------------------------------------------------------- */

async function fillList( section, ids, { emptyEl = null } = {} ) {
	const list = section.querySelector( '[data-my-list]' );
	if ( ! ids.length ) {
		list.replaceChildren();
		if ( emptyEl ) {
			emptyEl.hidden = false;
		}
		return 0;
	}
	// Grey placeholder cards while the real ones load.
	list.innerHTML =
		'<li class="card-skeleton" aria-hidden="true"><span class="card-skeleton__img"></span><span class="card-skeleton__line"></span><span class="card-skeleton__line"></span></li>'.repeat(
			Math.min( ids.length, 4 )
		);
	list.setAttribute( 'aria-busy', 'true' );
	try {
		const items = await fetchCards( ids );
		list.removeAttribute( 'aria-busy' );
		list.innerHTML = items.map( ( item ) => item.html ).join( '' ); // Same-origin card markup.
		if ( emptyEl ) {
			emptyEl.hidden = items.length > 0;
		}
		return items.length;
	} catch ( e ) {
		const p = document.createElement( 'p' );
		p.textContent = t.loadFailed;
		list.replaceWith( p );
		return 0;
	}
}

function initMyRecipes() {
	const saved = document.querySelector( '[data-my="saved"]' );
	if ( saved ) {
		fillList( saved, readList( SAVED ), {
			emptyEl: saved.querySelector( '[data-my-empty]' ),
		} );
	}

	const recent = document.querySelector( '[data-my="recent"]' );
	if ( recent ) {
		const ids = readList( RECENT );
		if ( ids.length ) {
			fillList( recent, ids ).then( ( n ) => {
				recent.hidden = n === 0;
			} );
		}
		const clear = recent.querySelector( '[data-my-clear]' );
		if ( clear ) {
			clear.addEventListener( 'click', () => {
				writeList( RECENT, [] );
				recent.querySelector( '[data-my-list]' ).replaceChildren();
				announce(
					recent.querySelector( '[data-my-status]' ),
					t.cleared
				);
				clear.hidden = true;
			} );
		}
	}
}

function initRecentHome() {
	const section = document.querySelector( '[data-recent-home]' );
	const ids = readList( RECENT ).slice( 0, 3 );
	if ( ! section || ! ids.length ) {
		return;
	}

	// Under the hero when that spot is below the fold (no layout shift in
	// view); otherwise it stays at the end of the page.
	const hero = document.querySelector( '.winonweb-home-hero' );
	if ( hero && hero.getBoundingClientRect().bottom > window.innerHeight ) {
		hero.after( section );
	}

	fillList( section, ids ).then( ( n ) => {
		section.hidden = n === 0;
	} );
}

/* -------------------------------------------------------------------- */
/* Instant search (combobox)                                             */
/* -------------------------------------------------------------------- */

function initSuggest( form, index ) {
	const input = form.querySelector( 'input[type="search"]' );
	if ( ! input || ! cfg.rest ) {
		return;
	}

	const list = document.createElement( 'ul' );
	list.id = `search-suggest-${ index }`;
	list.className = 'search-suggest';
	list.setAttribute( 'role', 'listbox' );
	list.setAttribute(
		'aria-label',
		( form.querySelector( 'label' ) || {} ).textContent || ''
	);
	list.hidden = true;

	const live = document.createElement( 'p' );
	live.className = 'screen-reader-text';
	live.setAttribute( 'aria-live', 'polite' );

	form.classList.add( 'has-suggest' );
	form.append( list, live );

	input.setAttribute( 'role', 'combobox' );
	input.setAttribute( 'aria-autocomplete', 'list' );
	input.setAttribute( 'aria-expanded', 'false' );
	input.setAttribute( 'aria-controls', list.id );

	const cache = new Map();
	let options = [];
	let active = -1;
	let timer = 0;
	let controller = null;

	const setActive = ( i ) => {
		options.forEach( ( o, n ) =>
			o.setAttribute( 'aria-selected', String( n === i ) )
		);
		active = i;
		if ( i >= 0 && options[ i ] ) {
			input.setAttribute( 'aria-activedescendant', options[ i ].id );
			options[ i ].scrollIntoView( { block: 'nearest' } );
		} else {
			input.removeAttribute( 'aria-activedescendant' );
		}
	};

	const close = () => {
		list.hidden = true;
		input.setAttribute( 'aria-expanded', 'false' );
		setActive( -1 );
	};

	const go = ( option ) => {
		track( 'search_suggestion', {
			to: option.dataset.submit ? 'results' : 'recipe',
		} );
		if ( option.dataset.submit ) {
			form.submit();
		} else {
			window.location.href = option.dataset.url;
		}
	};

	const render = ( q, items ) => {
		list.replaceChildren();
		options = [];

		items.forEach( ( item, i ) => {
			const li = document.createElement( 'li' );
			li.id = `${ list.id }-${ i }`;
			li.className = 'search-suggest__option';
			li.setAttribute( 'role', 'option' );
			li.dataset.url = item.url;
			if ( item.img ) {
				const img = document.createElement( 'img' );
				img.src = item.img;
				img.alt = '';
				img.width = 44;
				img.height = 44;
				img.decoding = 'async';
				li.append( img );
			}
			const text = document.createElement( 'span' );
			text.className = 'search-suggest__text';
			const title = document.createElement( 'span' );
			title.className = 'search-suggest__title';
			title.textContent = item.title;
			text.append( title );
			if ( item.meta ) {
				const meta = document.createElement( 'span' );
				meta.className = 'search-suggest__meta';
				meta.textContent = item.meta;
				text.append( meta );
			}
			li.append( text );
			options.push( li );
		} );

		const all = document.createElement( 'li' );
		all.id = `${ list.id }-all`;
		all.className = 'search-suggest__option search-suggest__option--all';
		all.setAttribute( 'role', 'option' );
		all.dataset.submit = '1';
		all.textContent = ( t.allResults || '{q}' ).replace( '{q}', q );
		options.push( all );

		options.forEach( ( o ) => {
			o.setAttribute( 'aria-selected', 'false' );
			o.addEventListener( 'mousedown', ( e ) => e.preventDefault() ); // Keep focus in the field.
			o.addEventListener( 'click', () => go( o ) );
			list.append( o );
		} );

		list.hidden = false;
		input.setAttribute( 'aria-expanded', 'true' );
		setActive( -1 );
		announce(
			live,
			items.length
				? ( t.results || '' ).replace( '{n}', String( items.length ) )
				: t.noResults
		);
	};

	const lookup = async () => {
		const q = input.value.trim();
		if ( q.length < 2 ) {
			close();
			return;
		}
		const key = q.toLowerCase();
		if ( cache.has( key ) ) {
			render( q, cache.get( key ) );
			return;
		}
		if ( controller ) {
			controller.abort();
		}
		controller = new AbortController();
		try {
			const res = await fetch(
				`${ cfg.rest }suggest?q=${ encodeURIComponent(
					q
				) }&lang=${ encodeURIComponent( cfg.lang || '' ) }`,
				{ signal: controller.signal }
			);
			const data = res.ok ? await res.json() : { items: [] };
			const items = Array.isArray( data.items ) ? data.items : [];
			cache.set( key, items );
			if ( input.value.trim() === q ) {
				render( q, items );
			}
		} catch ( e ) {
			// Aborted or offline: the normal search still works.
		}
	};

	input.addEventListener( 'input', () => {
		window.clearTimeout( timer );
		timer = window.setTimeout( lookup, 160 );
	} );

	input.addEventListener( 'keydown', ( e ) => {
		const open = ! list.hidden;
		if ( e.key === 'ArrowDown' ) {
			e.preventDefault();
			if ( ! open ) {
				lookup();
				return;
			}
			setActive( ( active + 1 ) % options.length );
		} else if ( e.key === 'ArrowUp' && open ) {
			e.preventDefault();
			setActive( active <= 0 ? options.length - 1 : active - 1 );
		} else if ( e.key === 'Enter' && open && active >= 0 ) {
			e.preventDefault();
			go( options[ active ] );
		} else if ( e.key === 'Escape' && open ) {
			e.stopPropagation(); // First Escape closes suggestions, the next one the search panel.
			close();
		} else if ( e.key === 'Tab' ) {
			close();
		}
	} );

	input.addEventListener( 'blur', () => window.setTimeout( close, 120 ) );
}

/* -------------------------------------------------------------------- */

export default function initEngage() {
	updateSavedCount();
	window.addEventListener(
		'storage',
		( e ) => e.key === SAVED && updateSavedCount()
	);

	initRecipePage();
	initMyRecipes();
	initRecentHome();
	document
		.querySelectorAll( 'form.search-form' )
		.forEach( ( form, i ) => initSuggest( form, i ) );
	initPantry();
	initMenu();
	initPwa();
}
